import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@clerk/react";
import { Minus, Plus, Lock, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { useApi, errMsg, pkr } from "@/lib/api";
import { useMe } from "@/context/MeContext";

export function FeeBreakdown({ q, label }) {
  return (
    <dl className="space-y-2 text-sm" data-testid="fee-breakdown">
      <div className="flex justify-between"><dt className="text-muted-foreground">{label}</dt><dd data-testid="fee-subtotal">{pkr(q.subtotal)}</dd></div>
      <div className="flex justify-between"><dt className="text-muted-foreground">GOSHA service fee ({q.service_fee_pct}%)</dt><dd data-testid="fee-service">{pkr(q.service_fee)}</dd></div>
      <div className="flex justify-between border-t border-border pt-2 text-base font-semibold"><dt>Total, all-in</dt><dd data-testid="fee-total">{pkr(q.total)}</dd></div>
    </dl>
  );
}

export default function BookingCard({ data }) {
  const { listing: l, quote_private } = data;
  const { isSignedIn } = useAuth();
  const { me } = useMe();
  const api = useApi();
  const nav = useNavigate();
  const [kind, setKind] = useState("individual");
  const [seats, setSeats] = useState(1);
  const [names, setNames] = useState([""]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (me?.name) setNames((n) => [n[0] || me.name, ...n.slice(1)]); }, [me]);
  useEffect(() => { setNames((n) => Array.from({ length: seats }, (_, i) => n[i] || "")); }, [seats]);

  const subtotal = l.price_pkr * seats;
  const fee = Math.round(subtotal * data.quote_individual.service_fee_pct / 100);
  const q = kind === "private" ? quote_private : { subtotal, service_fee: fee, total: subtotal + fee, service_fee_pct: data.quote_individual.service_fee_pct };
  const soldOut = l.spots_left === 0;

  const reserve = async () => {
    if (!isSignedIn) return nav("/sign-in");
    setBusy(true);
    try {
      const body = kind === "private" ? { listing_id: l.id, kind } : { listing_id: l.id, kind, seats, guests: names.map((n) => ({ name: n })) };
      const { data: b } = await api.post("/bookings", body);
      nav(`/checkout/${b.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const waitlist = async () => {
    if (!isSignedIn) return nav("/sign-in");
    try { await api.post(`/listings/${l.id}/waitlist`); toast.success("You're on the waitlist. The organizer will offer you a spot if one opens."); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div className="rounded-2xl border border-border bg-white p-6 shadow-[0_8px_32px_rgba(0,0,0,0.06)]" data-testid="booking-card">
      <div className="flex items-baseline justify-between">
        <div><span className="font-display text-3xl text-primary" data-testid="booking-price">{pkr(l.price_pkr)}</span><span className="text-sm text-muted-foreground"> / person</span></div>
      </div>
      <div className="mt-4">
        <div className="flex justify-between text-xs text-muted-foreground"><span data-testid="spots-left">{l.spots_left} of {l.capacity} spots left</span><span>{l.seats_sold} booked</span></div>
        <Progress value={(l.seats_sold / l.capacity) * 100} className="mt-2 h-1.5" />
      </div>
      {data.listing.private_available && (
        <div className="mt-5 grid grid-cols-2 gap-1 rounded-full bg-muted p-1 text-sm">
          {["individual", "private"].map((k) => (
            <button key={k} onClick={() => setKind(k)} className={`rounded-full py-2 transition-colors ${kind === k ? "bg-primary text-white" : "text-muted-foreground"}`} data-testid={`booking-kind-${k}`}>
              {k === "individual" ? "Book seats" : "Private buy-out"}
            </button>
          ))}
        </div>
      )}
      {kind === "individual" && !soldOut && (
        <div className="mt-5 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm">Seats</span>
            <div className="flex items-center gap-3">
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setSeats(Math.max(1, seats - 1))} data-testid="seats-decrease"><Minus className="h-3 w-3" /></Button>
              <span className="w-4 text-center tabular-nums" data-testid="seats-count">{seats}</span>
              <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => setSeats(Math.min(Math.min(6, l.spots_left), seats + 1))} data-testid="seats-increase"><Plus className="h-3 w-3" /></Button>
            </div>
          </div>
          {names.map((n, i) => (
            <Input key={i} value={n} placeholder={i === 0 ? "Your full name" : `Guest ${i + 1} full name`} onChange={(e) => setNames(names.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`guest-name-input-${i}`} />
          ))}
        </div>
      )}
      {kind === "private" && (
        <p className="mt-5 flex gap-2 rounded-lg bg-muted p-3 text-sm text-muted-foreground"><Users className="h-4 w-4 shrink-0" />Reserve all {l.capacity} spots for your group. You'll add guests from your portal after booking.</p>
      )}
      {!soldOut && <div className="mt-5"><FeeBreakdown q={q} label={kind === "private" ? `Private buy-out (${l.capacity} spots)` : `${pkr(l.price_pkr)} × ${seats}`} /></div>}
      {soldOut ? (
        <Button className="mt-6 w-full rounded-full" variant="outline" onClick={waitlist} data-testid="join-waitlist-btn">Join the waitlist</Button>
      ) : (
        <Button className="mt-6 w-full rounded-full bg-secondary py-6 text-base hover:bg-secondary/90" disabled={busy || (isSignedIn && kind === "individual" && names.some((n) => !n.trim()))} onClick={reserve} data-testid="reserve-spot-btn">
          {isSignedIn ? (kind === "private" ? "Reserve the whole getaway" : "Reserve spot") : "Sign in to reserve"}
        </Button>
      )}
      <p className="mt-4 flex gap-2 text-xs text-muted-foreground"><Lock className="h-3.5 w-3.5 shrink-0" />GOSHA holds your payment and releases it to the host only after the getaway is delivered.</p>
    </div>
  );
}
